const clone = value => JSON.parse(JSON.stringify(value));

const emptySelection = () => ({
  trackId: null,
  ids: [],
  primaryId: null,
  anchorId: null,
});

export const createAnnotationEditorState = () => ({
  toolMode: 'pencil',
  selection: emptySelection(),
  clipboard: null,
  copiedSourceIds: [],
  pastedIds: [],
  contextMenu: null,
  copyPopover: null,
  copyPopoverSuppressed: false,
});

export const createClipboard = ({ sheetId, sessionId, trackId, blocks }) => ({
  sheetId,
  sessionId,
  trackId,
  blocks: clone(blocks),
});

export const getContextMenuSelectionAction = ({ selection, trackId, annotationId }) => {
  if (!annotationId) return null;
  if (selection.trackId === trackId && selection.ids.includes(annotationId)) return null;
  return {
    type: 'REPLACE_SELECTION',
    trackId,
    ids: [annotationId],
    primaryId: annotationId,
    anchorId: annotationId,
  };
};

const replaceSelection = ({ trackId, ids, primaryId, anchorId }) => {
  const uniqueIds = [...new Set(ids || [])];
  if (!trackId || uniqueIds.length === 0) return emptySelection();
  return {
    trackId,
    ids: uniqueIds,
    primaryId: uniqueIds.includes(primaryId) ? primaryId : uniqueIds[0],
    anchorId: anchorId || uniqueIds[0],
  };
};

export const annotationEditorReducer = (state, action) => {
  switch (action.type) {
    case 'SET_TOOL':
      if (!['pencil', 'cursor'].includes(action.toolMode)) return state;
      return {
        ...state,
        toolMode: action.toolMode,
        ...(action.toolMode === 'pencil' ? {
          selection: emptySelection(),
          pastedIds: [],
          contextMenu: null,
          copyPopover: null,
          copyPopoverSuppressed: false,
        } : {}),
      };
    case 'REPLACE_SELECTION':
      return {
        ...state,
        selection: replaceSelection(action),
        pastedIds: [],
        contextMenu: null,
        copyPopoverSuppressed: false,
      };
    case 'TOGGLE_SELECTION': {
      if (state.selection.trackId !== action.trackId) {
        return {
          ...state,
          selection: replaceSelection({
            trackId: action.trackId,
            ids: [action.id],
            primaryId: action.id,
            anchorId: action.id,
          }),
          pastedIds: [],
          copyPopoverSuppressed: false,
        };
      }
      const contains = state.selection.ids.includes(action.id);
      const ids = contains
        ? state.selection.ids.filter(id => id !== action.id)
        : [...state.selection.ids, action.id];
      return {
        ...state,
        selection: replaceSelection({
          trackId: action.trackId,
          ids,
          primaryId: contains ? ids[0] : action.id,
          anchorId: state.selection.anchorId || action.id,
        }),
        pastedIds: [],
        copyPopoverSuppressed: false,
      };
    }
    case 'CLEAR_SELECTION':
      return {
        ...state,
        selection: emptySelection(),
        pastedIds: [],
        copyPopover: null,
        copyPopoverSuppressed: false,
      };
    case 'REMOVE_SELECTED_ID': {
      if (!state.selection.ids.includes(action.id)) return state;
      const ids = state.selection.ids.filter(id => id !== action.id);
      return {
        ...state,
        selection: ids.length ? replaceSelection({
          trackId: state.selection.trackId,
          ids,
          primaryId: ids.includes(state.selection.primaryId) ? state.selection.primaryId : ids[0],
          anchorId: ids.includes(state.selection.anchorId) ? state.selection.anchorId : ids[0],
        }) : emptySelection(),
        pastedIds: state.pastedIds.filter(id => id !== action.id),
        copyPopover: ids.length ? state.copyPopover : null,
        copyPopoverSuppressed: false,
      };
    }
    case 'DELETE_SELECTION_SUCCEEDED': {
      const deletedIds = new Set(action.ids || []);
      return {
        ...state,
        selection: emptySelection(),
        pastedIds: state.pastedIds.filter(id => !deletedIds.has(id)),
        contextMenu: null,
        copyPopover: null,
        copyPopoverSuppressed: false,
      };
    }
    case 'COPY_SELECTION':
      return {
        ...state,
        clipboard: clone(action.clipboard),
        copiedSourceIds: [...action.sourceIds],
        pastedIds: [],
        contextMenu: null,
      };
    case 'PASTE_SUCCEEDED':
      return {
        ...state,
        selection: replaceSelection({
          trackId: action.trackId,
          ids: action.ids,
          primaryId: action.ids[0],
          anchorId: action.ids[0],
        }),
        pastedIds: [...action.ids],
        contextMenu: null,
        copyPopover: null,
        copyPopoverSuppressed: false,
      };
    case 'DISMISS_TRANSIENT':
      return {
        ...state,
        selection: emptySelection(),
        pastedIds: [],
        contextMenu: null,
        copyPopover: null,
        copyPopoverSuppressed: false,
      };
    case 'OPEN_CONTEXT_MENU':
      return {
        ...state,
        contextMenu: clone(action.menu),
        copyPopover: null,
        copyPopoverSuppressed: true,
      };
    case 'CLOSE_CONTEXT_MENU':
      return { ...state, contextMenu: null };
    case 'SET_COPY_POPOVER':
      if (state.copyPopoverSuppressed) return state;
      if (
        state.copyPopover?.left === action.position?.left &&
        state.copyPopover?.top === action.position?.top
      ) return state;
      return { ...state, copyPopover: action.position ? clone(action.position) : null };
    case 'RESET_SHEET':
      return createAnnotationEditorState();
    default:
      return state;
  }
};

export const getCanPaste = (state, trackId) => Boolean(
  state.toolMode === 'cursor' &&
  state.clipboard?.blocks?.length &&
  state.clipboard.trackId === trackId
);

export const getClipboardPresentation = (state, trackId) => {
  const visible = state.toolMode === 'cursor' && state.clipboard?.trackId === trackId;
  return {
    isClipboardTrack: visible,
    copiedSourceIds: visible ? [...state.copiedSourceIds] : [],
  };
};
